import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Gift } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { useSubscription } from "@/features/billing/contexts/SubscriptionContext";
import { handleCheckout } from "@/shared/utils/checkoutHandler";
import { apiClient } from "@/shared/services/apiClient";

interface ReferralWelcomeModalProps {
  open: boolean;
  onClose: () => void;
  referrerName?: string; // Name of person who referred (optional)
  discountAmount?: number; // in dollars
  discountMonths?: number;
}

export function ReferralWelcomeModal({
  open,
  onClose,
  referrerName,
  discountAmount = 100,
  discountMonths = 3,
}: ReferralWelcomeModalProps) {
  const { toast } = useToast();
  const { refreshSubscription } = useSubscription();
  const [isNavigating, setIsNavigating] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const checkoutCleanupRef = useRef<(() => void) | null>(null);

  const monthlyPrice = 99;
  const discountedMonthlyPrice = (monthlyPrice - (discountAmount / discountMonths)).toFixed(2);

  // Mark modal as shown when it actually opens (not before)
  useEffect(() => {
    if (open) {
      console.log('[ReferralWelcomeModal] Modal opened for referred user:', {
        discountAmount,
        discountMonths,
        discountedMonthlyPrice
      });

      // Set localStorage to mark as shown (only show once)
      const markAsShown = async () => {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            const REFERRAL_MODAL_SHOWN_KEY = `referral_modal_shown_${user.id}`;
            localStorage.setItem(REFERRAL_MODAL_SHOWN_KEY, 'true');
            console.log('[ReferralWelcomeModal] ✅ Marked as shown (once only)');
          }
        } catch (error) {
          console.error('[ReferralWelcomeModal] Error marking modal as shown:', error);
        }
      };

      markAsShown();
    }
  }, [open, discountAmount, discountMonths, discountedMonthlyPrice]);

  // Cleanup checkout handler on unmount
  useEffect(() => {
    return () => {
      if (checkoutCleanupRef.current) {
        checkoutCleanupRef.current();
        checkoutCleanupRef.current = null;
      }
    };
  }, []);

  const handleSubscribeNow = async () => {
    console.log('[ReferralWelcomeModal] User clicked "Subscribe Now & Save" - creating Stripe checkout WITH REFERRAL');
    setIsNavigating(true);

    try {
      // Get current user and their referral code
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get referral code from user metadata or localStorage
      const referralCode = user.user_metadata?.referral_code || localStorage.getItem('pendingReferralCode');

      console.log('[ReferralWelcomeModal] Creating checkout with referral:', { referralCode, hasCode: !!referralCode });

      // Create Stripe checkout session for base subscription with referral discount
      const data = await apiClient.post<{ url?: string }>('/edge/create-checkout', {
        userCount: 1, // Single admin user subscription (wlkthru Pro)
        referral_code: referralCode, // ✅ Pass referral code to apply discount
        skip_trial: true, // ✅ Skip trial for referred users
        userData: {
          id: user.id,
          email: user.email,
          user_metadata: user.user_metadata
        }
      });

      if (!data?.url) {
        console.error('[ReferralWelcomeModal] No checkout URL returned');
        toast({
          title: "Error",
          description: "Failed to get checkout URL. Please try again.",
          variant: "destructive"
        });
        setIsNavigating(false);
        return;
      }

      console.log('[ReferralWelcomeModal] Opening Stripe checkout with $100 discount applied:', data.url);

      // Close modal and show processing state
      onClose();
      setIsNavigating(false);
      setProcessingPayment(true);

      // Use unified checkout handler
      const { cleanup } = handleCheckout({
        checkoutUrl: data.url,
        onSuccess: async () => {
          console.log('[ReferralWelcomeModal] Payment successful!');

          // Immediately hide processing modal
          setProcessingPayment(false);

          // Show success toast
          toast({
            title: "Subscription Activated!",
            description: "Your referral discount has been applied. Welcome to wlkthru Pro!",
          });

          // Refresh subscription in background
          try {
            await refreshSubscription();
          } catch (error) {
            console.error('[ReferralWelcomeModal] Error refreshing subscription:', error);
          }
        },
        onError: (errorMessage) => {
          console.log('[ReferralWelcomeModal] ❌ Payment failed:', errorMessage);
          toast({
            title: "Payment Failed",
            description: errorMessage || "Please try again or contact support.",
            variant: "destructive",
          });
          setProcessingPayment(false);
        },
        onClose: () => {
          console.log('[ReferralWelcomeModal] Checkout window closed');
          setProcessingPayment(false);
          refreshSubscription();
        },
      });

      // Store cleanup function
      checkoutCleanupRef.current = cleanup;
    } catch (err: any) {
      console.error('[ReferralWelcomeModal] Exception creating checkout:', err);
      toast({
        title: "Error",
        description: err.message || "Something went wrong. Please try again.",
        variant: "destructive"
      });
      setIsNavigating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full">
              <Gift className="h-8 w-8 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center text-2xl font-bold">
            {referrerName && referrerName !== 'Unknown' && referrerName !== 'A friend'
              ? `${referrerName} invited you to wlkthru!`
              : 'Welcome to wlkthru!'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Discount message - moved referral box under title */}
          <div className="text-center space-y-2">
            <div className="bg-primary/10 border border-primary/30 rounded-lg p-3">
              <p className="text-xs font-semibold text-primary">
                You've been gifted $100 in wlkthru credit!
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={handleSubscribeNow}
              disabled={isNavigating || processingPayment}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 text-lg shadow-lg transition-all duration-200"
            >
              {isNavigating ? "Loading..." : processingPayment ? "Processing..." : "Subscribe Now & Save $100"}
            </Button>
          </div>

          <p className="text-xs text-center text-gray-500">
            Your discount will be applied to your first {discountMonths} monthly payments
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
